/**
 * Stammdaten: Packstücktypen (ADR 0028).
 *
 * Kürzel und Bezeichnung, etwa GB Gitterbox. Das Kürzel steht vorn in der
 * Kennung (GB-09) und bleibt deshalb, wie es ist. Typen werden nie gelöscht,
 * nur stillgelegt.
 *
 * Darunter die Packstücke ohne Typ -- meist solche, deren Kennung zu keinem
 * Kürzel passte. Ein Tipp ordnet sie zu.
 *
 * Nur für Administratoren und nur mit Netz (ADR 0006).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  PackstueckTypKuerzel,
  type Lagerort,
  type PackstueckTyp,
  type PackstueckTypListe,
} from "@thw/contracts";
import { anfrage, sitzungLesen } from "../../api/klient.js";
import { useOnline } from "../../api/online.js";
import { abgleichen } from "../../lokal/abgleich.js";
import { useBestand } from "../../lokal/bestand.js";
import {
  Eingabe,
  Karte,
  Knopf,
  Ladeanzeige,
  NurMitNetz,
  Rueckmeldung,
  Schalter,
} from "../../ui/bausteine.jsx";

const SCHLUESSEL = ["packstueck-typen"] as const;

type Meldung = { art: "gut" | "warnung"; text: string } | null;

export function PackstueckTypVerwaltung() {
  const online = useOnline();
  const bestand = useBestand();
  const zwischenspeicher = useQueryClient();
  const [meldung, setMeldung] = useState<Meldung>(null);
  const [kuerzel, setKuerzel] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");

  const { data, isPending } = useQuery({
    queryKey: SCHLUESSEL,
    queryFn: () => anfrage<PackstueckTypListe>("GET", "/api/core/packstueck-typen"),
    enabled: online,
  });

  const fehler = (f: unknown): void => {
    setMeldung({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
  };

  const anlegen = useMutation({
    mutationFn: () =>
      anfrage<PackstueckTyp>("POST", "/api/core/packstueck-typen", {
        kuerzel,
        bezeichnung: bezeichnung.trim(),
      }),
    onSuccess: (t) => {
      setMeldung({ art: "gut", text: `✓ ${t.kuerzel} · ${t.bezeichnung} angelegt` });
      setKuerzel("");
      setBezeichnung("");
      void zwischenspeicher.invalidateQueries({ queryKey: SCHLUESSEL });
    },
    onError: fehler,
  });

  const aendern = useMutation({
    mutationFn: ({ k, aenderung }: { k: string; aenderung: Partial<PackstueckTyp> }) =>
      anfrage<PackstueckTyp>("PATCH", `/api/core/packstueck-typen/${k}`, aenderung),
    onSuccess: (t) => {
      setMeldung({ art: "gut", text: `✓ ${t.kuerzel} gespeichert` });
      void zwischenspeicher.invalidateQueries({ queryKey: SCHLUESSEL });
    },
    onError: fehler,
  });

  const zuordnen = useMutation({
    mutationFn: ({ ort, typ }: { ort: Lagerort; typ: string }) =>
      anfrage<Lagerort>("PATCH", `/api/core/lagerorte/${ort.id}`, { packstueck_typ: typ }),
    onSuccess: (l) => {
      setMeldung({ art: "gut", text: `✓ ${l.kennung} ist jetzt ${l.packstueck_typ ?? ""}` });
      void abgleichen({ stammdaten: true });
    },
    onError: fehler,
  });

  if (sitzungLesen()?.benutzer.rolle !== "administrator") {
    return <Rueckmeldung art="warnung" kopf="Nur für Administratoren" />;
  }

  const typen = data?.packstueck_typen ?? [];
  const aktive = typen.filter((t) => t.aktiv);
  const kuerzelFalsch = kuerzel !== "" && !PackstueckTypKuerzel.safeParse(kuerzel).success;
  const vergeben = typen.some((t) => t.kuerzel === kuerzel);
  // Eine lokale Kopie von vor Vertrag 2.12 kennt das Feld nicht -- dann gilt es als leer.
  const ohneTyp =
    bestand === undefined
      ? []
      : [...bestand.lagerorte.values()]
          .filter((l) => l.artikel_id !== null && l.aktiv && (l.packstueck_typ ?? null) === null)
          .sort((a, b) => a.kennung.localeCompare(b.kennung, "de"));

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Packstücktypen</h1>
      <p className="text-neutral-600 dark:text-neutral-300">
        Das Kürzel steht vorn in der Kennung: GB-09 ist eine Gitterbox. Beim Anlegen eines
        Packstücks schlägt die App die nächste freie Nummer vor.
      </p>
      {!online && <NurMitNetz />}
      {meldung !== null && <Rueckmeldung art={meldung.art} kopf={meldung.text} />}

      {online && isPending ? (
        <Ladeanzeige was="Die Packstücktypen" />
      ) : (
        <ul className="flex flex-col gap-2" aria-label="Typen">
          {typen.map((t) => (
            <TypZeile
              key={t.kuerzel}
              typ={t}
              online={online}
              speichern={(aenderung) => {
                aendern.mutate({ k: t.kuerzel, aenderung });
              }}
            />
          ))}
        </ul>
      )}

      <Karte titel="Neuer Typ">
        <Eingabe
          beschriftung="Kürzel"
          wert={kuerzel}
          aendern={(neu) => {
            setKuerzel(
              neu
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "")
                .slice(0, 6),
            );
          }}
          hinweis={
            kuerzelFalsch
              ? "1 bis 6 Großbuchstaben oder Ziffern."
              : vergeben
                ? `${kuerzel} gibt es schon.`
                : "Etwa GB — bleibt, auch wenn sich die Bezeichnung ändert."
          }
        />
        <Eingabe beschriftung="Bezeichnung" wert={bezeichnung} aendern={setBezeichnung} />
        <Knopf
          art="haupt"
          disabled={
            !online ||
            kuerzel === "" ||
            kuerzelFalsch ||
            vergeben ||
            bezeichnung.trim() === "" ||
            anlegen.isPending
          }
          onClick={() => {
            anlegen.mutate();
          }}
        >
          Anlegen
        </Knopf>
      </Karte>

      {ohneTyp.length > 0 && (
        <section aria-label="Packstücke ohne Typ" className="flex flex-col gap-2">
          <h2 className="text-lg font-bold">Packstücke ohne Typ ({ohneTyp.length})</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Ihre Kennung passt zu keinem Kürzel. Ein Tipp ordnet sie zu.
          </p>
          <ul className="flex flex-col gap-2">
            {ohneTyp.map((l) => (
              <li
                key={l.id}
                className="flex flex-col gap-2 rounded-xl bg-white p-3 dark:bg-neutral-800"
              >
                <span className="font-semibold">
                  {l.kennung} — {l.bezeichnung}
                </span>
                <div className="flex flex-wrap gap-2">
                  {aktive.map((t) => (
                    <button
                      key={t.kuerzel}
                      type="button"
                      disabled={!online || zuordnen.isPending}
                      aria-label={`${l.kennung} ist ${t.bezeichnung}`}
                      onClick={() => {
                        zuordnen.mutate({ ort: l, typ: t.kuerzel });
                      }}
                      className="min-h-12 rounded-full bg-blue-100 px-4 font-semibold text-blue-950 active:bg-blue-200 disabled:opacity-40 dark:bg-blue-950 dark:text-blue-100"
                    >
                      {`${t.kuerzel} · ${t.bezeichnung}`}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function TypZeile({
  typ,
  online,
  speichern,
}: {
  typ: PackstueckTyp;
  online: boolean;
  speichern: (aenderung: Partial<PackstueckTyp>) => void;
}) {
  const [bezeichnung, setBezeichnung] = useState(typ.bezeichnung);
  return (
    <li
      className={`flex flex-col gap-2 rounded-xl bg-white p-3 dark:bg-neutral-800 ${typ.aktiv ? "" : "opacity-60"}`}
    >
      <span className="font-mono text-lg font-bold">{typ.kuerzel}</span>
      <Eingabe beschriftung="Bezeichnung" wert={bezeichnung} aendern={setBezeichnung} />
      <Schalter
        beschriftung="Aktiv"
        hinweis="Stillgelegt steht der Typ beim Anlegen nicht mehr zur Wahl."
        an={typ.aktiv}
        gesperrt={!online}
        aendern={(an) => {
          speichern({ aktiv: an });
        }}
      />
      <Knopf
        disabled={!online || bezeichnung.trim() === "" || bezeichnung.trim() === typ.bezeichnung}
        onClick={() => {
          speichern({ bezeichnung: bezeichnung.trim() });
        }}
      >
        Speichern
      </Knopf>
    </li>
  );
}
